import { useMemo, useState } from "react";
import { Card } from "../ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Badge } from "../ui/badge";
import { RequirementCard } from "../requirementss/RequirementCard";
import { RequirementForm, RequirementFormValues } from "../requirements/RequirementForm";
// import { DashboardFilters } from "./DashboardFilters";
import { DashboardFilters } from "../dashboard/DashboardFilters";
import { SummaryCards } from "./SummaryCards";
import { LoggedInFilter } from "./StatusToggleBar";
import { Requirement, FilterState, RequirementStatus } from "../../types/deals";
import { Customer as CustomerType, CustomerFilterState } from "../../types/customer";
import { Inbox, LogIn, Search, XCircle } from "lucide-react";
import { useEffect } from "react";
import { RMList } from "../../utils/interfaces";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../hooks/use-toast";
import apiService from "../../services/apiService";

interface LoginDashboardProps {
  requirements: Requirement[];
  filters: FilterState;
  customers: CustomerType[];
  customerFilters: CustomerFilterState;
  onRequirementAdd: (values: RequirementFormValues) => void;
  onRequirementUpdate: (updated: Requirement) => void;
  onFiltersChange: (filters: FilterState) => void;
  onCustomerAdd: (customer: Omit<CustomerType, 'id' | 'createdAt' | 'createdBy'>) => void;
  onCustomerUpdate: (customer: CustomerType) => void;
  setCustomerFilters: (filters: CustomerFilterState) => void;
  requirementStatuses: RequirementStatus[];
  uniqueRmNames: RMList[];
  summary: any;
  currentPageFilter?: "logged-in" | "to-be-logged-in" | "dropped";
  onCurrentPageFilterChange?: (v: "logged-in" | "to-be-logged-in" | "dropped") => void;
  lender_user_product_types: any[]
  LenderStatus: any
  lenderCompanyList: any[]
}

// Get derived lender status for a requirement
function getLenderDerivedStatus(req: Requirement): string {
  if (req.lenders.length === 0) return "open";
  const allStatuses = req.lenders.map(l => l.statusHistory[l.statusHistory.length - 1]?.status).filter(Boolean);
  if (allStatuses.some(s => s === "9")) return "disbursed";
  if (allStatuses.some(s => s === "8")) return "sanctioned";
  if (allStatuses.every(s => s === "7")) return "rejected";
  return "pending";
}

function getRequirementStatus(req: Requirement): string {
  if (req.lenders.length === 0) return "Open";
  const allStatuses = req.lenders.map(l => l.statusHistory[l.statusHistory.length - 1]?.status).filter(Boolean);
  if (allStatuses.some(s => s === "9")) return "Disbursed";
  if (allStatuses.some(s => s === "8") && !allStatuses.some(s => s === "9")) return "Sanctioned";
  if (allStatuses.some(s => s === "6")) return "Term Sheet Shared";
  if (allStatuses.every(s => s === "7")) return "Rejected";
  if (allStatuses.some(s => s === "3")) return "Under Review";
  if (allStatuses.some(s => s === "2")) return "Credit Discussion Completed";
  return allStatuses[0] || "Open";
}

export function LoginDashboard({ 
  requirements, filters, customers, customerFilters, 
  onRequirementAdd, onRequirementUpdate, onFiltersChange, 
  onCustomerAdd, onCustomerUpdate, setCustomerFilters,
  requirementStatuses,
  uniqueRmNames,
  summary,
  currentPageFilter = "logged-in",
  onCurrentPageFilterChange,
  lender_user_product_types,
  LenderStatus,
  lenderCompanyList,
}: LoginDashboardProps) {
  const navigate = useNavigate();
  const [formOpen, setFormOpen] = useState(false);
  const [loggedInFilter, setLoggedInFilter] = useState<LoggedInFilter>("all");
  const { toast } = useToast();
  // Cmd+K shortcut to focus search
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        const input = document.getElementById("dashboard-search-input") as HTMLInputElement;
        input?.focus();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Filter requirements based on current filters



  // Split into logged in / to be logged in / dropped
  const loggedIn = useMemo(() => requirements?.filter(r => r.lenders.length > 0) || [], [requirements]);
  const toBeLoggedIn = useMemo(() => requirements?.filter(r => r.lenders.length === 0 && (String(r.status) !== "6" && String(r.status) !== "7") ) || [], [requirements]);
  const droppedCases = useMemo(() => requirements?.filter(r => r.lenders.length === 0 && (String(r.status) === "6" || String(r.status) === "7")), [requirements]);

  // Apply logged-in tab filters
  const filteredLoggedIn = useMemo(() => {
    let list = loggedIn || [];
    if (loggedInFilter !== "all") {
      list = list.filter(r => getLenderDerivedStatus(r) === loggedInFilter);
    }
    return list;
  }, [loggedIn, loggedInFilter]);

  function handleAddRequirement(values: RequirementFormValues) {
    onRequirementAdd(values);
    setFormOpen(false);
  }

  async function handleExport() {

    const response = await apiService.post<any>('/login/export', { filters });

    const csvContent = [
      [
        'Requirement ID',
        'Customer Name',
        'Type',
        'Products',
        'Created At',
        'Quantum',
        'Tenure',
        'Lender Name',
        'Status'
      ],
      ...response?.data?.map((req: any) => {
        return [
          req.requirementId,
          req.entityName || '',
          req.productType || '',
          req.products || "",  // ✅ should now display correctly
          new Date(req.createdAt).toLocaleDateString("en-GB") || "",
          req.proposedQuantum?.toString() || '',
          req.tenure?.toString() || '',
          req.institution?.toString() || '',
          req.statusName || ""
        ];
      })
    ]
      .map(row => row.map((field: any) => `"${field}"`).join(','))
      .join('\n');


    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `requirements-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  function handleCardClick(filter: 'totalReq' | 'toBeLoggedIn' | 'stagnantReq' | 'rejectedReq' | 'sanctionedReq' | 'disbursedReq') {
    let newFilters = { ...filters };
    switch (filter) {
      case 'toBeLoggedIn': newFilters.statuses = ['To Be Logged In']; break;
      case 'stagnantReq': newFilters.showStagnant = true; break;
      case 'rejectedReq': newFilters.statuses = ['Rejected']; break;
      case 'sanctionedReq': newFilters.statuses = ['Sanctioned']; newFilters.requirementTypes = ['Hybrid']; break;
      case 'disbursedReq': newFilters.statuses = ['Disbursed']; break;
      default:
        newFilters = { search: "", products: [], statuses: [], applicationRMs: [], dateRange: {}, lenderInstitutions: [], lenderNames: [], requirementTypes: [], showStagnant: false, customerType: [], currentPageFilter: (filters as any).currentPageFilter ?? "loggedIn" } as any;
        break;
    }
    onFiltersChange(newFilters);
  }


  const handleViewRequirement = (requirement:any) => {
    // Navigate to customer detail page using customerId
    if (requirement.customer.id) {
      navigate(`/manage-customer/${requirement.customer.id}`, {
        state: { tab: "login-requirement-view", requirementDetails: requirement },
      });
    } else {
      toast({
        title: "Error",
        description: "Customer ID not found for this requirement",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen w-[100%] bg-white">
      <Tabs defaultValue="requirements" className="w-full">
        <TabsContent value="requirements" className="mt-0">
          <div className="px-6 py-6 space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1">
                <h1 className="text-2xl font-semibold">Login Dashboard</h1>
                <p className="text-muted-foreground text-sm">Manage all customer requirements and tagged lenders</p>
              </div>
              <div className="flex items-center gap-2">
                {/* <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    id="dashboard-search-input"
                    type="text"
                    placeholder="Search cases..."
                    value={filters.search}
                    onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
                    className="h-9 w-[260px] rounded-md border bg-background pl-9 pr-12 text-sm outline-none focus:ring-2 focus:ring-ring"
                  />
                  <kbd className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none hidden sm:inline-flex h-5 items-center gap-0.5 rounded border bg-muted px-1.5 text-[10px] font-medium text-muted-foreground">
                    ⌘K
                  </kbd>
                </div> */}

            <DashboardFilters
              filters={filters}
              onFiltersChange={onFiltersChange}
              requirements={requirements}
              onExport={handleExport}
              uniqueRmNames={uniqueRmNames}
              lender_user_product_types={lender_user_product_types}
              LenderStatus={LenderStatus}
              lenderCompanyList={lenderCompanyList}
            />
              </div>
            </div>

            <SummaryCards data={summary} onCardClick={handleCardClick} />

            {/* Inner CRM Sub-Tabs */}
            <Tabs
              value={currentPageFilter}
              onValueChange={(v) => onCurrentPageFilterChange?.(v as any)}
              className="w-full"
            >
              <TabsList className="w-full max-w-md grid grid-cols-3 h-9">
                <TabsTrigger value="logged-in" className="text-xs gap-1.5">
                  <LogIn className="h-3.5 w-3.5" />
                  Logged In
                  <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-[10px]">{summary?.loggedIn}</Badge>
                </TabsTrigger>
                <TabsTrigger value="to-be-logged-in" className="text-xs gap-1.5">
                  <Inbox className="h-3.5 w-3.5" />
                  To Be Logged In
                  <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-[10px]">{summary?.toBeLoggedIn}</Badge>
                </TabsTrigger>
                <TabsTrigger value="dropped" className="text-xs gap-1.5">
                  <XCircle className="h-3.5 w-3.5" />
                  Dropped
                  <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-[10px]">{summary?.droppedCases}</Badge>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="logged-in" className="mt-3 space-y-3">
                {/* <StatusToggleBar
                  filter={loggedInFilter} onFilterChange={setLoggedInFilter}
                  counts={toggleCounts}
                /> */}
                {filteredLoggedIn.length === 0 ? (
                  <Card className="p-8 text-center">
                    <LogIn className="h-10 w-10 mx-auto mb-2 text-muted-foreground/40" />
                    <h2 className="text-lg font-semibold">No logged-in cases found</h2>
                    <p className="text-muted-foreground text-sm mt-1">
                      {loggedIn.length === 0 ? "No requirements have lenders tagged yet." : "Try adjusting your filters."}
                    </p>
                  </Card>
                ) : (
                  <div className="space-y-2">
                    {filteredLoggedIn.map(req => (
                      <RequirementCard key={req.id} req={req} onUpdate={onRequirementUpdate} showCustomer={true} requirementStatuses={requirementStatuses} uniqueRmNames={uniqueRmNames} onView={handleViewRequirement} lenderStatusList={LenderStatus} />
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="to-be-logged-in" className="mt-3 space-y-3">
                {/* {Object.keys(toBeLoggedInStats).length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(toBeLoggedInStats).map(([status, count]) => (
                      <Badge key={status} variant="outline" className="text-xs">
                        {status} ({count})
                      </Badge>
                    ))}
                  </div>
                )} */}
                {toBeLoggedIn.length === 0 ? (
                  <Card className="p-8 text-center">
                    <Inbox className="h-10 w-10 mx-auto mb-2 text-muted-foreground/40" />
                    <h2 className="text-lg font-semibold">All cases are logged in!</h2>
                    <p className="text-muted-foreground text-sm mt-1">Every requirement has lenders tagged.</p>
                  </Card>
                ) : (
                  <div className="space-y-2">
                    {toBeLoggedIn.map(req => (
                      <RequirementCard key={req.id} req={req} onUpdate={onRequirementUpdate} showCustomer={true} requirementStatuses={requirementStatuses} uniqueRmNames={uniqueRmNames} onView={handleViewRequirement} lenderStatusList={LenderStatus} />
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="dropped" className="mt-3 space-y-3">
                {droppedCases.length === 0 ? (
                  <Card className="p-8 text-center">
                    <XCircle className="h-10 w-10 mx-auto mb-2 text-muted-foreground/40" />
                    <h2 className="text-lg font-semibold">No dropped cases</h2>
                    <p className="text-muted-foreground text-sm mt-1">No requirements with CL/CI rejected status.</p>
                  </Card>
                ) : (
                  <div className="space-y-2">
                    {droppedCases.map(req => (
                      <RequirementCard key={req.id} req={req} onUpdate={onRequirementUpdate} showCustomer={true} requirementStatuses={requirementStatuses} uniqueRmNames={uniqueRmNames} onView={handleViewRequirement} lenderStatusList={LenderStatus}
                      
                      />
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>

          <RequirementForm
            open={formOpen}
            onOpenChange={setFormOpen}
            onSubmit={handleAddRequirement}
            uniqueRmNames={uniqueRmNames}
            lender_user_product_types={[]}
            LenderStatus={[]}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
